import { useState, useEffect, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@cloudscape-design/components/button'
import Box from '@cloudscape-design/components/box'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Container from '@cloudscape-design/components/container'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import Input from '@cloudscape-design/components/input'
import Modal from '@cloudscape-design/components/modal'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Alert from '@cloudscape-design/components/alert'
import Page from '@/components/common/Page'
import { useAccount, useUpdateAccountPlatform, useDisconnectAccountPlatform, useTestAccountPlatform, useSetDefaultEmailProvider } from '@/hooks/useAccount'
import { sendgridCredentialsSchema } from '@/lib/schemas'
import type { SendgridCredentialsFormData } from '@/lib/schemas'
import type { PlatformName } from '@/lib/types'
import { useNotificationStore } from '@/state/notificationStore'
import { useAuthStore } from '@/state/authStore'
import { getPlatformLabel, getPlatformIcon } from '@/utils/platform'
import { formatDate } from '@/utils/format'

const ALL_PLATFORMS: PlatformName[] = [
  'google', 'gmail', 'outlook', 'facebook',
  'twitter', 'linkedin',
]

declare global {
  interface Window {
    FB: {
      init(params: { appId: string; cookie: boolean; xfbml: boolean; version: string }): void
      login(
        callback: (response: { authResponse?: { accessToken: string }; status: string }) => void,
        options: { config_id: string }
      ): void
      getLoginStatus(callback: (response: { status: string; authResponse?: { accessToken: string } }) => void): void
    }
    fbAsyncInit?: () => void
  }
}

let fbInitialized = false

export default function PlatformsPage() {
  const { account } = useAccount()
  const { updatePlatform, loading: updatingPlatform } = useUpdateAccountPlatform()
  const { disconnectPlatform, loading: disconnecting } = useDisconnectAccountPlatform()
  const { testPlatform, loading: testing, result: testResult } = useTestAccountPlatform()
  const { setDefaultEmailProvider, loading: settingDefault } = useSetDefaultEmailProvider()

  const [disconnectTarget, setDisconnectTarget] = useState<PlatformName | null>(null)
  const [showSendgridModal, setShowSendgridModal] = useState(false)

  useEffect(() => {
    if (fbInitialized) {
      return
    }
    window.fbAsyncInit = () => {
      if (!fbInitialized && import.meta.env.VITE_FACEBOOK_APP_ID) {
        window.FB.init({
          appId: import.meta.env.VITE_FACEBOOK_APP_ID,
          cookie: true,
          xfbml: false,
          version: 'v21.0',
        })
        fbInitialized = true
      }
    }
    const script = document.createElement('script')
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.defer = true
    document.body.appendChild(script)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const platform = params.get('platform')
    const status = params.get('status')
    const message = params.get('message')
    if (!platform || !status) return
    useNotificationStore.getState().add({
      type: status === 'connected' ? 'success' : 'error',
      content: status === 'connected'
        ? `${getPlatformLabel(platform as PlatformName)} connected successfully`
        : (message ?? 'Connection failed. Please try again.'),
      dismissible: true,
    })
    const clean = new URL(window.location.href)
    clean.searchParams.delete('platform')
    clean.searchParams.delete('status')
    clean.searchParams.delete('message')
    window.history.replaceState({}, '', clean.toString())
  }, [])

  const handleFacebookLogin = useCallback(
    (platform: 'facebook' | 'instagram') => {
      const user = useAuthStore.getState().user
      if (!user?.uid) {
        useNotificationStore.getState().add({
          type: 'error',
          content: 'Not authenticated. Please log in.',
          dismissible: true,
        })
        return
      }
      if (!window.FB) {
        useNotificationStore.getState().add({
          type: 'error',
          content: 'Facebook SDK not ready. Try again.',
          dismissible: true,
        })
        return
      }
      window.FB.login(
        (response) => {
          if (!response.authResponse?.accessToken) {
            useNotificationStore.getState().add({
              type: 'error',
              content: 'Facebook login cancelled or failed.',
              dismissible: true,
            })
            return
          }
          fetch(
            `${import.meta.env.VITE_API_BASE_URL}/api/auth/${platform}/exchange`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                accessToken: response.authResponse.accessToken,
                uid: user.uid,
              }),
            }
          )
            .then((res) => res.json())
            .then((data) => {
              if (!data.ok) {
                useNotificationStore.getState().add({
                  type: 'error',
                  content: data.error || 'Failed to connect. Try again.',
                  dismissible: true,
                })
              } else {
                useNotificationStore.getState().add({
                  type: 'success',
                  content: `${platform === 'facebook' ? 'Facebook' : 'Instagram'} connected successfully`,
                  dismissible: true,
                })
                window.location.reload()
              }
            })
            .catch((err) => {
              useNotificationStore.getState().add({
                type: 'error',
                content: err instanceof Error ? err.message : 'Connection failed',
                dismissible: true,
              })
            })
        },
        { config_id: '2030714201212842' }
      )
    },
    []
  )

  const handlePlatformConnect = (platform: PlatformName) => {
    if (platform === 'sendgrid') {
      setShowSendgridModal(true)
      return
    }
    if (platform === 'facebook' || platform === 'instagram') {
      handleFacebookLogin(platform)
      return
    }
    const user = useAuthStore.getState().user
    if (!user?.uid) {
      useNotificationStore.getState().add({
        type: 'error',
        content: 'Not authenticated. Please log in.',
        dismissible: true,
      })
      return
    }
    const returnTo = `${window.location.origin}/settings/platforms`
    window.location.href =
      `${import.meta.env.VITE_API_BASE_URL}/api/auth/${platform}` +
      `?uid=${encodeURIComponent(user.uid)}&redirect_uri=${encodeURIComponent(returnTo)}`
  }

  const handleDisconnectPlatform = (platform: PlatformName) => {
    setDisconnectTarget(null)
    disconnectPlatform(platform)
  }

  const handleSendgridSubmit = (data: SendgridCredentialsFormData) => {
    setShowSendgridModal(false)
    updatePlatform('sendgrid', data)
  }

  const { control, handleSubmit, formState: { errors }, reset } = useForm<SendgridCredentialsFormData>({
    resolver: zodResolver(sendgridCredentialsSchema),
  })

  const handleSendgridModalSubmit = (data: SendgridCredentialsFormData) => {
    handleSendgridSubmit(data)
    reset()
  }

  return (
    <Page
      title="Platform Connections"
      helpContent={{
        header: 'Platform Integrations',
        body: (
          <div>
            <p>Connect and manage integrations with external platforms. Authorize Sabar to access your social media and marketing tools.</p>
            <h4>Supported Platforms:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li><strong>Google</strong> - Analytics, Search Console, Business Profile</li>
              <li><strong>Facebook</strong> - Business account and page management</li>
              <li><strong>LinkedIn</strong> - Company page posting and insights</li>
              <li><strong>Twitter</strong> - Account posting and engagement</li>
              <li><strong>Gmail</strong> - Email delivery and reply tracking</li>
              <li><strong>Outlook</strong> - Email delivery via Microsoft account</li>
              <li><strong>SendGrid</strong> - Email delivery service API key</li>
            </ul>
            <h4>How to Use:</h4>
            <p>Click connect on each platform and follow the OAuth flow. Test the connection to verify. Disconnect anytime to revoke access.</p>
          </div>
        ),
      }}
    >
      <Container
        header={<Header variant="h2">Default Email Sender</Header>}
      >
        <SpaceBetween size="m">
          <DefaultEmailProviderPicker
            platforms={account?.platforms ?? []}
            defaultEmailProvider={account?.defaultEmailProvider ?? null}
            onSelect={setDefaultEmailProvider}
            loading={settingDefault}
          />
        </SpaceBetween>
      </Container>
      <Container>
        <ColumnLayout columns={4}>
          {ALL_PLATFORMS.map(p => (
            <PlatformCard
              key={p}
              platform={p}
              connection={account?.platforms?.find(c => c.platform === p)}
              onConnect={handlePlatformConnect}
              onDisconnect={() => setDisconnectTarget(p)}
              onTest={() => testPlatform(p)}
              testing={testing}
              testResult={testResult?.platform === p ? testResult : null}
            />
          ))}
        </ColumnLayout>

        <Modal
          visible={disconnectTarget !== null}
          header={`Disconnect ${disconnectTarget ? getPlatformLabel(disconnectTarget) : ''}?`}
          onDismiss={() => setDisconnectTarget(null)}
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDisconnectTarget(null)}>Cancel</Button>
              <Button variant="normal" loading={disconnecting}
                onClick={() => { handleDisconnectPlatform(disconnectTarget!); setDisconnectTarget(null) }}>
                Disconnect
              </Button>
            </SpaceBetween>
          }
        >
          <Box>This will remove the connection. You can reconnect at any time.</Box>
        </Modal>

        <Modal
          visible={showSendgridModal}
          header="Connect SendGrid"
          onDismiss={() => { setShowSendgridModal(false); reset() }}
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => { setShowSendgridModal(false); reset() }}>Cancel</Button>
              <Button variant="primary" loading={updatingPlatform} onClick={() => { handleSubmit(handleSendgridModalSubmit)() }}>Connect</Button>
            </SpaceBetween>
          }
        >
          <Form>
            <SpaceBetween size="m">
              <FormField label="API Key" errorText={errors.api_key?.message}>
                <Controller
                  name="api_key"
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input type="password" value={value ?? ''} onChange={(e) => onChange(e.detail.value)} onBlur={onBlur} />
                  )}
                />
              </FormField>
              <FormField label="From Email" errorText={errors.from_email?.message}>
                <Controller
                  name="from_email"
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input value={value ?? ''} onChange={(e) => onChange(e.detail.value)} onBlur={onBlur} />
                  )}
                />
              </FormField>
              <FormField label="From Name (optional)" errorText={errors.from_name?.message}>
                <Controller
                  name="from_name"
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input value={value ?? ''} onChange={(e) => onChange(e.detail.value)} onBlur={onBlur} />
                  )}
                />
              </FormField>
            </SpaceBetween>
          </Form>
        </Modal>
      </Container>
    </Page>
  )
}

interface PlatformCardProps {
  platform: PlatformName
  connection: { platform: string; connected: boolean; connectedAt: string | null; email?: string | null } | undefined
  onConnect: (p: PlatformName) => void
  onDisconnect: (p: PlatformName) => void
  onTest: (p: PlatformName) => void
  testing: boolean
  testResult: { success: boolean; error?: string | null } | null
}

function PlatformCard({ platform, connection, onConnect, onDisconnect, onTest, testing, testResult }: PlatformCardProps) {
  const connected = connection?.connected ?? false
  const IconComponent = getPlatformIcon(platform)
  return (
    <Container>
      <SpaceBetween size="s">
        <Box variant="strong">
          <IconComponent size={20} /> {getPlatformLabel(platform)}
        </Box>

        <StatusIndicator type={connected ? 'success' : 'stopped'}>
          {connected ? `Connected · ${formatDate(connection!.connectedAt!)}` : 'Not connected'}
        </StatusIndicator>

        {connected && connection?.email && (
          <Box variant="small" color="text-body-secondary">{connection.email}</Box>
        )}

        {testResult && (
          <Alert type={testResult.success ? 'success' : 'error'} dismissible>
            {testResult.success
              ? platform === 'gmail' || platform === 'outlook'
                ? `Test email sent to ${connection?.email ?? 'your inbox'} — check your inbox`
                : 'Connection is working'
              : (testResult.error ?? 'Connection failed')}
          </Alert>
        )}

        <SpaceBetween direction="horizontal" size="xs">
          {connected ? (
            <>
              <Button variant="normal" loading={testing} onClick={() => onTest(platform)}>
                Test
              </Button>
              <Button variant="link" onClick={() => onDisconnect(platform)}>
                Disconnect
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => onConnect(platform)}>
              Connect
            </Button>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  )
}

interface DefaultEmailProviderPickerProps {
  platforms: Array<{ platform: string; connected: boolean; email?: string | null }>
  defaultEmailProvider: string | null
  onSelect: (provider: string) => void
  loading: boolean
}

const EMAIL_PLATFORMS = ['gmail', 'outlook'] as const

function DefaultEmailProviderPicker({ platforms, defaultEmailProvider, onSelect, loading }: DefaultEmailProviderPickerProps) {
  const connectedEmailPlatforms = platforms.filter(
    (p) => p.connected && p.email && (EMAIL_PLATFORMS as readonly string[]).includes(p.platform),
  )

  const options = connectedEmailPlatforms.map((p) => ({
    value: p.platform,
    label: `${getPlatformLabel(p.platform as PlatformName)} — ${p.email}`,
  }))

  const selectedOption = options.find((o) => o.value === defaultEmailProvider) ?? null

  if (connectedEmailPlatforms.length === 0) {
    return (
      <Box color="text-status-inactive">
        No email providers connected. Connect Gmail or Outlook above to set a default sender.
      </Box>
    )
  }

  return (
    <FormField
      label="Default email sender"
      description="Outreach emails will be sent from this account."
    >
      <Select
        selectedOption={selectedOption}
        options={options}
        onChange={({ detail }) => { onSelect(detail.selectedOption.value ?? '') }}
        disabled={loading}
        placeholder="Select a connected email account"
      />
    </FormField>
  )
}
